import { fetchQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { MINUTES } from '@sequencehq/utils/dates'
import { apiQueryClient } from 'features/api/apiQueryClient'
import { IntegrationsPortErrors } from 'modules/Integrations/domain'
import { getCachedIntegrations } from 'modules/Integrations/utils/getCachedIntegrations'
import { isLiveCustomerPushToggle } from 'Integrations/integrationsConfig/common/AutoLinkCustomers/entityTypeSyncToggle.helpers'
import { PaymentBankAccountsByCurrency } from 'Integrations/integrationsConfig/common/PaymentBankAccounts/paymentBankAccounts.types'
import {
  QuickBooksConfig,
  QuickBooksTaxCodeMapping
} from 'modules/Integrations/integrationsConfig/quickBooks/quickbooks.integration.config'

export const quickBooksConfigurationLoader = async (): Promise<{
  data: QuickBooksConfig | null
  error: IntegrationsPortErrors | null
}> => {
  let integrationsResponse
  try {
    integrationsResponse = await getCachedIntegrations()
  } catch {
    return {
      data: null,
      error: IntegrationsPortErrors.Other
    }
  }

  const integration = integrationsResponse?.items.find(
    int => int.service === 'QuickBooks_Online'
  )

  // If QBO is not connected, dont load the configuration
  if (integration?.state !== 'CONNECTED') {
    return {
      data: {
        importCustomerWorkflow: integration?.enabled ?? false,
        taxExemptTaxCode: '',
        taxableTaxCode: '',
        reverseChargedTaxCode: '',
        taxCodeMappings: {},
        defaultAccount: '',
        autoLinkCustomers: false,
        autoLinkSyncPermission: undefined,
        paymentBankAccounts: {}
      },
      error: null
    }
  }

  const [
    defaultTaxCodesData,
    defaultAccountData,
    mappingsData,
    togglesResponse,
    // Shares the query cache with the management screen's read of the bank account options.
    fullIntegration
  ] = await Promise.all([
    dashboardv99990101Client.getDefaultQuickbooksTaxCodes(),
    dashboardv99990101Client.getDefaultQuickbooksAccount(),
    dashboardv99990101Client.getQuickbooksTaxCodeMappings(),
    dashboardv99990101Client.getEntityTypeSyncToggles(),
    // A failed read degrades to no saved accounts instead of erroring the whole load.
    fetchQuery(
      apiQueryClient,
      dashboard20240730Client.getIntegration,
      'QuickBooks_Online',
      { staleTime: 1 * MINUTES }
    ).catch(() => null)
  ])

  const autoLinkToggle =
    togglesResponse.data?.items.find(
      toggle =>
        toggle.integrationService === 'QuickBooks_Online' &&
        isLiveCustomerPushToggle(toggle)
    ) ?? null
  const autoLinkCustomers = autoLinkToggle?.enabled ?? false
  const autoLinkSyncPermission = autoLinkToggle?.syncPermission

  const defaultTaxCodes = defaultTaxCodesData?.data ?? {}
  const defaultAccount = defaultAccountData?.data?.standard?.id ?? ''
  const mappings = mappingsData?.data?.items ?? []

  const settings =
    fullIntegration?.service === 'QuickBooks_Online'
      ? fullIntegration.settings
      : null

  const paymentBankAccounts = (settings?.paymentBankAccounts ?? []).reduce(
    (acc, { currency, bankAccountId }) =>
      bankAccountId ? { ...acc, [currency]: bankAccountId } : acc,
    {} as PaymentBankAccountsByCurrency
  )

  const mappingsByTaxCategoryIdMap = mappings.reduce(
    (acc, mapping) => {
      acc[mapping.taxCategoryId] = mapping
      return acc
    },
    {} as Record<string, QuickBooksTaxCodeMapping>
  )

  return {
    data: {
      importCustomerWorkflow: integration?.enabled ?? false,
      taxExemptTaxCode: defaultTaxCodes.exempt?.id ?? '',
      taxableTaxCode: defaultTaxCodes.taxable?.id ?? '',
      reverseChargedTaxCode: defaultTaxCodes.reverseCharged?.id ?? '',
      taxCodeMappings: mappingsByTaxCategoryIdMap,
      defaultAccount,
      autoLinkCustomers,
      autoLinkSyncPermission,
      paymentBankAccounts
    },
    error: null
  }
}
